import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { BaseView } from '@/components/base'
import {
  SendBackground,
  SendActionButtons,
  SendCurrencyBottomSheet,
  SendHeader,
  SendContent,
} from '@/features/send-money'
import { useSendLogic } from '@/features/send-money'
import { useSendStore, getCurrencyColors } from '@/store/useSendStore'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { WalletCurrency } from '@/domain'
import SafeAreaLayout from '@/components/layout/SafeAreaLayout'
import { useAppAnalytics } from '@/services/analytics'

export const SendRequestScreen: React.FC = () => {
  const location = useLocation()
  const { resetSend } = useSendLogic()
  const isCurrencyModalOpen = useSendStore((state) => state.isCurrencyModalOpen)
  const setIsCurrencyModalOpen = useSendStore(
    (state) => state.setIsCurrencyModalOpen,
  )
  const setSelectedCurrency = useSendStore((state) => state.setSelectedCurrency)
  const setCryptoAmount = useSendStore((state) => state.setCryptoAmount)
  const setFiatAmount = useSendStore((state) => state.setFiatAmount)
  const setType = useSendStore((state) => state.setType)
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )
  const fiatAmount = useSendStore((state) => state.fiatAmount)
  const { trackEvent, events } = useAppAnalytics()

  // Update fiatAmount when displayCurrency changes
  useEffect(() => {
    if (displayCurrency && fiatAmount.code !== displayCurrency.isin) {
      setFiatAmount({
        amount: fiatAmount.amount,
        code: displayCurrency.isin,
      })
    }
  }, [displayCurrency, fiatAmount.code, fiatAmount.amount, setFiatAmount])

  // Initialize with provided currency or reset state
  useEffect(() => {
    const locationState = location.state as any
    const currency = locationState?.currency as WalletCurrency
    const type = locationState?.type || 'send'
    const mode = locationState?.mode || 'send'

    if (currency && currency.code) {
      // Initialize with provided currency
      const currencyData = {
        code: currency.code,
        name: currency.name || currency.displayCurrencyCode || currency.code,
        icon: currency.icon,
        // Use colors from provided currency or get standard colors
        ...getCurrencyColors(currency.code),
        ...(currency.avatarBGRefColor && {
          avatarBGRefColor: currency.avatarBGRefColor,
        }),
        ...(currency.buttonBGRefColor && {
          buttonBGRefColor: currency.buttonBGRefColor,
        }),
        ...(currency.buttonIconRefColor && {
          buttonIconRefColor: currency.buttonIconRefColor,
        }),
        ...(currency.lineChartRefColor && {
          lineChartRefColor: currency.lineChartRefColor,
        }),
      }

      setSelectedCurrency(currencyData)
      setCryptoAmount({
        amount: '0',
        code: currency.code,
      })
      setFiatAmount({
        amount: '0',
        code: displayCurrency?.isin || 'USD',
      })
      setType(type)

      trackEvent(events.MONEY_ACTION_STARTED, {
        action_type: type === 'request' ? 'request' : 'send',
        source: 'home',
      })
    } else {
      // Reset to initial state if currency not provided
      resetSend()
      // Track start of flow (default send)
      trackEvent(events.MONEY_ACTION_STARTED, {
        action_type: 'send',
        source: 'home',
      })
    }
  }, [
    location.state,
    resetSend,
    setSelectedCurrency,
    setCryptoAmount,
    setFiatAmount,
    setType,
    setType,
    displayCurrency,
    trackEvent,
    events.MONEY_ACTION_STARTED,
  ])

  const handleCloseCurrencyModal = () => {
    setIsCurrencyModalOpen(false)
  }

  return (
    // Full-screen container - exact copy of RN structure
    <BaseView
      className="h-screen flex flex-col overflow-hidden"
      data-component="send-wrapper"
    >
      <SafeAreaLayout isStretch hasNavBar>
        {/* Gradient background */}
        <SendBackground />

        {/* Main content - copy of RN BaseSafeAreaView */}
        <BaseView
          flex={1}
          className="relative z-10 flex flex-col overflow-hidden"
          px={12}
          style={{
            paddingTop: 'env(safe-area-inset-top)', // Safe area support for iOS
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            minHeight: 0, // Important for flex containers
          }}
        >
          {/* Header with icons */}
          <SendHeader />

          {/* Main content */}
          <SendContent />
          {/*  */}
        </BaseView>
        <SendActionButtons />

        {/* Currency selection modal */}
        <SendCurrencyBottomSheet
          isOpen={isCurrencyModalOpen}
          onClose={handleCloseCurrencyModal}
        />
      </SafeAreaLayout>
    </BaseView>
  )
}
